import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { APP_CONFIG } from '../config/version';

export interface VersionInfo {
  currentVersion: string;
  latestVersion: string;
  isUpdateAvailable: boolean;
  updateUrl: string | null;
  releaseNotes: string | null;
}

/**
 * Polls the `checkAppVersion` Cloud Function (which reports the latest deployed
 * version) and exposes whether the running build is out of date. Adapted from
 * the AESA (transport-platform-new) hook: checks on mount, on a background
 * interval, on window focus and when coming back online, with a 30s minimum
 * between calls and a debounce so we never spam the function.
 */
export const useVersionCheck = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState(false);

  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkVersionRef = useRef<(() => Promise<void>) | null>(null);

  const MIN_CHECK_INTERVAL = 30 * 1000;

  const checkVersion = useCallback(async () => {
    if (!APP_CONFIG.versionCheck.autoCheck) return;
    if (!navigator.onLine) {
      setFunctionsAvailable(false);
      return;
    }
    if (isCheckingRef.current) return;

    const now = Date.now();
    if (now - lastCheckTimeRef.current < MIN_CHECK_INTERVAL) return;

    isCheckingRef.current = true;
    setIsChecking(true);
    lastCheckTimeRef.current = now;

    try {
      const fn = httpsCallable<{ currentVersion: string }, VersionInfo>(
        functions,
        'checkAppVersion',
      );
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 10000),
      );
      const result = (await Promise.race([
        fn({ currentVersion: APP_CONFIG.version }),
        timeout,
      ])) as { data: VersionInfo };

      setFunctionsAvailable(true);
      const data = result.data;
      if (data.currentVersion === data.latestVersion) {
        // Up to date → clear any stale "dismissed" marker.
        localStorage.removeItem('versionUpdateDismissed');
      }
      setVersionInfo(data);
    } catch {
      // Function unavailable / timeout / offline — keep the banner hidden.
      setFunctionsAvailable(false);
    } finally {
      setIsChecking(false);
      isCheckingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkVersionRef.current = checkVersion;
  }, [checkVersion]);

  const debouncedCheckVersion = useCallback(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      checkVersionRef.current?.();
    }, 2000);
  }, []);

  // Initial check (once).
  useEffect(() => {
    if (APP_CONFIG.versionCheck.autoCheck) checkVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background interval (created once).
  useEffect(() => {
    if (!APP_CONFIG.versionCheck.autoCheck) return;
    const id = setInterval(() => {
      if (navigator.onLine) checkVersionRef.current?.();
    }, APP_CONFIG.versionCheck.checkInterval);
    return () => clearInterval(id);
  }, []);

  // Re-check on focus / online.
  useEffect(() => {
    if (!APP_CONFIG.versionCheck.autoCheck) return;
    const onFocus = () => {
      if (navigator.onLine && functionsAvailable) debouncedCheckVersion();
    };
    const onOnline = () => debouncedCheckVersion();
    const onOffline = () => setFunctionsAvailable(false);

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [functionsAvailable, debouncedCheckVersion]);

  return { versionInfo, isChecking, functionsAvailable, checkVersion };
};
